<template>
    <div v-if="shouldShow">
        <template v-if="isReady">
            <video ref="videoPlayer" class="video-js vjs-16-9" preload="metadata" controls playsinline :autoplay="autoPlay"></video>
        </template>
        <template v-else>
            <b-alert variant="warning" class="mb-3" show>
                <p>
                    Sorry, we're encountering some issues with the video at the moment.
                    Please check back again shortly.
                </p>
            </b-alert>
        </template>
    </div>
</template>

<script>
import videojs from 'video.js';
import '../videojs/SkipButton.js';
import { globalEventFire } from '../../tracker.js';
import axios from 'axios'; // Ensure axios is imported

export default {
    props: {
        type: {
            type: String,
            required: true,
        },
        entity: {
            type: Object,
            default: null,
            required: true,
        },
        // Whether to track minutes and completion, we don't do this on free videos pre-auth
        track: {
            type: Boolean,
            default: true,
            required: false,
        },
        // Whether to fire a 'start class' call against our API
        trackStart: {
            type: Boolean,
            default: false,
            required: false,
        },
        autoPlay: {
            type: Boolean,
            default: false,
            required: false,
        },
        nextVideoUrl: { // New optional prop
            type: String,
            default: null,
        },
        startFrom: {
            type: Number,
            default: 0,
        },
        enablePreviewLimit: {
            type: Boolean,
            default: false,
        },
        previewSeconds: {
            type: Number,
            default: 0,
        },
    },

    data() {
        return {
            isReady: true,
            player: null,
            hasStartAnalyticsFired: false,
            hasEndAnalyticsFired: false,
            shouldShow: true,
            keydownHandler: null, // Reference to the handler for removal
        }
    },

    mounted() {
        this.getVideoUrl();

        this.$nextTick(() => { // Use arrow function to preserve 'this'
            require('videojs-vtt-thumbnails-freetube');
            this.player = videojs(this.$refs.videoPlayer, {
                playbackRates: [1, 1.2, 1.5, 2], // Define desired playback speeds
                controlBar: {
                    children: [
                        'playToggle',
                        'playbackRateMenuButton',
                        'volumePanel',
                        'currentTimeDisplay',
                        'progressControl',
                        'durationDisplay',
                        'fullscreenToggle',
                    ],
                },
            });

            // If nextVideoUrl exists, pass it as an option to the SkipButton
            if (this.nextVideoUrl) {
                this.player.getChild('controlBar').addChild('SkipButton', { nextVideoUrl: this.nextVideoUrl }, 1); // Insert after playToggle (index 0)
            }
        });

        if(this.type == 'quick-class') {
            this.track = true;
        }

        // Define the keydown handler
        this.keydownHandler = this.handleKeydown;

        // Add the keydown event listener
        window.addEventListener('keydown', this.keydownHandler);
    },

    beforeDestroy() {
        // Remove the keydown event listener when the component is destroyed
        window.removeEventListener('keydown', this.keydownHandler);

        // Dispose the video.js player to free up resources
        if (this.player) {
            this.player.dispose();
        }
    },

    methods: {
        // Handle keydown events
        handleKeydown(event) {
            // Ignore if the target is an input, textarea, select, or contenteditable element
            const ignoreTags = ['INPUT', 'TEXTAREA', 'SELECT'];
            if (ignoreTags.includes(event.target.tagName) || event.target.isContentEditable) {
                return; // Do nothing if typing in an input field
            }

            // Check if the space bar was pressed
            if (event.code === 'Space') {
                // Prevent default behavior like page scrolling
                event.preventDefault();

                // Ensure the player is initialized
                if (this.player) {
                    if (this.player.paused()) {
                        this.player.play();
                    } else {
                        this.player.pause();
                    }
                }
            }
        },

        // Get a viewing key
        getVideoUrl: function() {
            let uri = '';
            if(this.type == 'vod-class') {
                uri = '/api/vod_classes/' + this.entity.id + '/video'
            } else if(this.type == 'artist-session') {
                uri = '/api/artist-session/' + this.entity.id + '/video'
            } else if(this.type == 'exam-enrolment-admin-start') {
                uri = '/api/exam-enrolment/' + this.entity.id + '/video/admin-start-video'
            } else if(this.type == 'exam-enrolment-admin-end') {
                uri = '/api/exam-enrolment/' + this.entity.id + '/video/admin-end-video'
            } else if(this.type == 'exam-enrolment-examiner-summary') {
                uri = '/api/exam-enrolment/' + this.entity.id + '/video/examiner-summary-video'
            } else if(this.type == 'exam-enrolment-module-admin-start') {
                uri = '/api/exam-enrolment-user-progress/' + this.entity.id + '/video/admin-start-video'
            } else if(this.type == 'exam-enrolment-examiner-question-feedback') {
                uri = '/api/exam-enrolment-user-answer/' + this.entity.id + '/video/examiner-summary-video'
            } else if(this.type == 'exam-enrolment-user-answer') {
                uri = '/api/exam-enrolment-user-answer/' + this.entity.id + '/video/answer-video'
            } else if(this.type == 'artist-feedback-enrolment-summary') {
                uri = '/api/feedback_enrolment/' + this.entity.id + '/video/artist-feedback-enrolment-summary'
            } else if(this.type == 'quick-class') {
                uri = '/api/quick-classes/' + this.entity.id + '/video'
            }

            axios.get(uri)
                .then((response) => {
                    // Video ready?
                    this.isReady = response.data.isReady;

                    if (!this.isReady) {
                        return;
                    }

                    // Video source
                    this.player.src([
                        {
                            type: "application/x-mpegURL",
                            src: response.data.hls
                        }
                    ]);

                    // Set the start time
                    this.player.currentTime(this.startFrom);

                    // Preload the storyboard
                    const storyboard = new Image();
                    storyboard.src = response.data.storyboard;
                    const storyboardImg = new Image();
                    storyboardImg.src = response.data.storyboard_img;

                    // Trickplay/scrubber seeker bar
                    this.player.vttThumbnails({
                        src: response.data.storyboard,
                        showTimestamp: true
                    });

                    let timer = null,
                        totalTime = 0;

                    // On video start, inform analytics. On the VOD class pages this happens in the controller
                    this.player.on('play', () => {
                        if(this.track) {
                            timer = window.setInterval(() => { // Use arrow function to preserve 'this'
                                totalTime += 1;
                            }, 1000);

                            if(!this.hasStartAnalyticsFired) {
                                if(this.type == 'vod-class') {
                                    globalEventFire('entityVideoStart', 'VOD', {
                                        'name': this.entity.name +' on '+ this.entity.course.name
                                    });

                                    if(this.trackStart) {
                                        axios.get('/api/vod_classes/' + this.entity.id + '/video/start');
                                    }
                                }

                                if(this.type == 'quick-class') {
                                    axios.get('/api/quick-classes/' + this.entity.id + '/video/start');
                                }

                                this.hasStartAnalyticsFired = true;
                            }
                        }
                    });

                    // Clear timer on pause
                    this.player.on('pause', () => {
                        if (timer) clearInterval(timer);
                    });

                    // Check if current time watched is 80% through
                    var percentageInSeconds = 0;
                    this.player.on('loadedmetadata', () => {
                        percentageInSeconds = this.player.duration() * 0.8;
                    });

                    // Every minute track a play event
                    var debounce = true;
                    var seconds = 60;
                    var nonce = this.makeNonce(10);

                    this.player.on('timeupdate', (event) => {
                        if(this.track) {
                            var hasPlayedTime = this.player.currentTime();
                            var intPlayedTime = parseInt(hasPlayedTime, 10);

                            // Minute by minute tracker
                            var isMinute = intPlayedTime % seconds === 0 && intPlayedTime !== 0;

                            // Ten second by ten second tracker
                            var isTenSeconds = intPlayedTime % 10 === 0 && intPlayedTime !== 0;

                            // Handle VOD classes
                            if(this.type == 'vod-class') {
                                // Every minute track a play event for VOD classes
                                if (isMinute) {
                                    if(debounce) {
                                        debounce = false;
                                        axios.get('/api/vod_classes/' + this.entity.vod_course_id + '/' + this.entity.id + '/video/minute?token=' + nonce);

                                        setTimeout(() => { // Use arrow function
                                            debounce = true;
                                        }, 10000);
                                    }
                                }

                                // Check if current time watched is 80% through
                                if(totalTime >= percentageInSeconds) {
                                    if(!this.hasEndAnalyticsFired) {
                                        axios.get('/api/vod_classes/' + this.entity.id + '/video/complete');

                                        globalEventFire('entityVideoEnd', 'VOD', {
                                            'name': this.entity.name +' on '+ this.entity.course.name
                                        });
                                        this.hasEndAnalyticsFired = true;
                                    }
                                }
                            }

                            // Handle Quick Classes
                            if(this.type == 'quick-class') {
                                // Every 10 seconds track a play event for Quick Classes
                                if (isTenSeconds) {
                                    if (debounce) {
                                        debounce = false;
                                        axios.get('/api/quick-classes/' + this.entity.id + '/video/track-seconds?token=' + nonce + '&time=' + intPlayedTime);

                                        setTimeout(() => { // Use arrow function
                                            debounce = true;
                                        }, 2000);
                                    }
                                }

                                // Check if current time watched is 80% through
                                if(totalTime >= percentageInSeconds) {
                                    if(!this.hasEndAnalyticsFired) {
                                        axios.get('/api/quick-classes/' + this.entity.id + '/video/complete');
                                        this.hasEndAnalyticsFired = true;
                                    }
                                }
                            }
                        }

                        if (this.enablePreviewLimit && this.previewSeconds > 0) {
                            if (this.player.currentTime() >= this.previewSeconds) {
                                this.player.pause();
                                this.player.dispose();
                                const endedEvent = new CustomEvent('previewLimitReached', {
                                    detail: {
                                        type: this.type,
                                        entityId: this.entity.id,
                                    },
                                });
                                window.dispatchEvent(endedEvent);
                            }
                        }
                    });

                    this.player.on('ended', (event) => {
                        if(this.track) {
                            if(this.type == 'quick-class') {
                                var hasPlayedTime = this.player.currentTime();
                                var intPlayedTime = parseInt(hasPlayedTime, 10);

                                axios.get('/api/quick-classes/' + this.entity.id + '/video/track-seconds?token=' + nonce + '&time=' + intPlayedTime + '&finished=true');
                            }
                        }

                        const endedEvent = new CustomEvent('videoEnded', {
                            detail: {
                                type: this.type,
                                entityId: this.entity.id,
                            },
                        });
                        window.dispatchEvent(endedEvent);
                    });
                })
                .catch((error) => {
                    this.shouldShow = false;
                })

        },

        // Make a random string for our token
        makeNonce: function(length) {
            var result = '';
            var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            var charactersLength = characters.length;
            for ( var i = 0; i < length; i++ ) {
                result += characters.charAt(Math.floor(Math.random() * charactersLength));
            }
            return result;
        }
    }
}
</script>
